import { TRANSFER_COST_DECIMAL_PRECISION } from "../constants"
import type { TransferLaidDownCostFormValues } from "../schemas/laidDownCost"

import type { Transfer } from "@/graphql/codegen/graphql"
import { roundToPrecision } from "@/utils/number"
import { isNil } from "@/utils/utils"

type TransferCostValues = Pick<
  TransferLaidDownCostFormValues,
  "freightMiscCost" | "warehousingFees" | "monthlyStorageAccrual" | "inboundHandlingCost" | "outboundHandlingCost"
>

export const calculateFreightMiscCost = (costPerUom: number | null, totalQuantity: number): number | null =>
  isNil(costPerUom) ? null : roundToPrecision(costPerUom * totalQuantity, TRANSFER_COST_DECIMAL_PRECISION)

export const calculateFreightMiscCostPerUom = (cost: number | null, totalQuantity: number): number | null =>
  isNil(cost) ? null : roundToPrecision(cost / totalQuantity, TRANSFER_COST_DECIMAL_PRECISION)

export const calculateTotalTransferCost = ({
  freightMiscCost,
  warehousingFees,
  monthlyStorageAccrual,
  inboundHandlingCost,
  outboundHandlingCost,
}: TransferCostValues): number =>
  roundToPrecision(
    (freightMiscCost ?? 0) +
      (warehousingFees ?? 0) +
      (monthlyStorageAccrual ?? 0) +
      (inboundHandlingCost ?? 0) +
      (outboundHandlingCost ?? 0),
    TRANSFER_COST_DECIMAL_PRECISION
  )

export const calculateTransferCostPerUom = (totalCost: number, totalQuantity: number): number =>
  roundToPrecision(totalCost / totalQuantity, TRANSFER_COST_DECIMAL_PRECISION)

export const calculateExtendedLaidDownCostPerUom = (
  originalCostPerUom: number | null,
  transferCostPerUom: number
): number | null =>
  isNil(originalCostPerUom)
    ? null
    : roundToPrecision(originalCostPerUom + transferCostPerUom, TRANSFER_COST_DECIMAL_PRECISION)

export const toTransferLaidDownCostFormValues = (transfer: Transfer): TransferLaidDownCostFormValues => ({
  // TODO(AK-6102): Seed from the saved transfer costs once the backend exposes them.
  freightMiscCostPerUom: null,
  freightMiscCost: null,
  warehousingFees: null,
  monthlyStorageAccrual: null,
  inboundHandlingCost: null,
  outboundHandlingCost: null,
  // TODO(AK-6102): Seed originalCostPerUom from the source GR / warehouse laid down cost once the backend exposes it.
  products: transfer.products.map(({ productId }) => ({ productId, originalCostPerUom: null })),
  isCostComplete: false,
})
